import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Clock, Mail, ArrowRight } from 'lucide-react'
import travelAgentService from '../services/travelAgentService'
import LoadingSpinner from '../components/LoadingSpinner'

const ApplicationPendingPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const applicationId = location.state?.applicationId

  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(!!applicationId)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!applicationId) return
    let cancelled = false
    const load = async () => {
      try {
        const res = await travelAgentService.getApplicationStatus(applicationId)
        if (!cancelled && res?.status === 'success' && res?.data) {
          setStatus(res.data)
        } else if (!cancelled) {
          setError(res?.message || 'Could not load application status')
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || 'Could not load application status')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [applicationId])

  const statusLabel =
    status?.applicationStatus === 'under_review'
      ? 'Under review'
      : status?.applicationStatus === 'pending'
        ? 'Pending verification'
        : status?.applicationStatus
          ? status.applicationStatus.replace(/_/g, ' ')
          : 'Under review'

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-amber-50 p-4">
              <Clock className="h-10 w-10 text-amber-600" />
            </div>
          </div>

          <h1 className="text-center text-2xl font-poppinsSemiBold text-neutral-900">
            Application under review
          </h1>
          <p className="mt-3 text-center text-sm text-neutral-600 font-poppinsRegular leading-relaxed">
            Thank you for registering with Axonall Studio. Our team is reviewing your application.
            You will receive an email when your account is approved.
          </p>

          {loading && (
            <div className="mt-6 flex justify-center">
              <LoadingSpinner size="md" />
            </div>
          )}

          {error && (
            <p className="mt-4 text-center text-sm text-red-600">{error}</p>
          )}

          {status && !loading && (
            <div className="mt-6 rounded-xl border border-neutral-100 bg-neutral-50 p-4 text-sm space-y-2">
              {status.applicationId && (
                <p>
                  <span className="text-neutral-500">Application ID:</span>{' '}
                  <span className="font-poppinsMedium text-neutral-900">{status.applicationId}</span>
                </p>
              )}
              <p>
                <span className="text-neutral-500">Status:</span>{' '}
                <span className="font-poppinsMedium capitalize text-neutral-900">{statusLabel}</span>
              </p>
              {status.email && (
                <p className="flex items-center gap-2 text-neutral-700">
                  <Mail className="h-4 w-4 shrink-0 text-neutral-400" />
                  {status.email}
                </p>
              )}
            </div>
          )}

          {!applicationId && !loading && (
            <p className="mt-4 text-center text-xs text-neutral-500">
              Sign in with the email you used to register. After email verification, use the link on
              the login page to check status if you have your application ID.
            </p>
          )}

          <div className="mt-8 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-neutral-950 py-3 text-xs font-medium tracking-[0.15em] text-white hover:bg-black"
            >
              Back to sign in
              <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/register"
              className="text-center text-sm text-primary-900 hover:text-primary-700 font-poppinsMedium underline"
            >
              Start a new application
            </Link>
          </div>
        </div>
      </div>
      <p className="pb-6 text-center text-xs text-neutral-400">© 2026 Axonall. All rights reserved.</p>
    </div>
  )
}

export default ApplicationPendingPage
